// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import { ErrorRounded } from '@/constants/hardfin-standard-icons';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { AssetLocationReportGroupControl } from '@/report/components/asset-location-report-group-control';
import { AssetLocationReportTable } from '@/report/components/asset-location-report-table';
import { AssetPresenceReportEducationalTitle } from '@/report/components/asset-presence-report-educational-title';
import { AssetReportLoader } from '@/report/components/asset-report-loader';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportDateRangeInput } from '@/report/components/report-date-range-input';
import {
  ASSET_PRESENCE_REPORT_DATE_RANGE_SHORTCUTS,
  ASSET_PRESENCE_REPORT_MAX_DAYS,
} from '@/report/constants/asset-presence-date-range';
import { useAssetLocationReport } from '@/report/hooks/use-asset-location-report';
import { getReportFileName } from '@/report/utilities/get-report-file-name';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const groupSchema = z.enum(['location', 'product']);

type Group = z.infer<typeof groupSchema>;

const DEFAULT_GROUP: Group = 'location';

const REPORT_TITLE = 'Field asset report';

const searchSchema = z.object({
  group: groupSchema.optional(),
  start: z.string().optional(),
  end: z.string().optional(),
});

// A stable identity, so the grid does not rebuild its row tree.
const EMPTY_ROWS: never[] = [];

export const Route = createFileRoute('/_app/reports/field')({
  component: FieldReportRoute,
  validateSearch: searchSchema,
});

function FieldReportRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { user } = useCurrentUser();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useResizeObserver(containerRef);

  // Only run the report query when a date range is explicitly selected
  const hasDateRange = search.start !== undefined && search.end !== undefined;

  const fieldReport = useAssetLocationReport({
    type: 'field',
    start: search.start,
    end: search.end,
    enabled: hasDateRange,
  });

  // Track loader animation completion state
  const [isLoaderComplete, setIsLoaderComplete] = useState(false);

  // Reset loader when search params change
  useEffect(() => {
    setIsLoaderComplete(false);
  }, [search.start, search.end]);

  // Show loader during initial load, hide once complete
  const showLoader = fieldReport.isLoading && !isLoaderComplete;

  const handleLoaderComplete = () => {
    setIsLoaderComplete(true);
  };

  const data = fieldReport.data?.rows ?? EMPTY_ROWS;
  const isDateRangeError =
    fieldReport.isError &&
    fieldReport.error !== null &&
    handleAPIError(fieldReport.error).errorType === 'date_range_exceeded';

  const group = search.group ?? DEFAULT_GROUP;

  const dayCount = fieldReport.interval.count('days');
  const isDateRangeTooLong = dayCount > ASSET_PRESENCE_REPORT_MAX_DAYS;

  const handleChangeDateRange = (dateRange: DateRange<DateTime>) => {
    const start = dateRange[0]?.toISODate();
    const end = dateRange[1]?.toISODate();

    navigate({ search: { ...search, start, end } });
  };

  const handleChangeGroup = (group: Group) => {
    navigate({ search: { ...search, group } });
  };

  const getFileName = useCallback(() => {
    return getReportFileName('field-asset-report', user.organization.timezone);
  }, [user.organization.timezone]);

  return (
    <>
      <PageContent
        sx={{
          gap: 2,
          flex: 1,
          display: 'grid',
          maxHeight: '100%',
          gridTemplateRows: 'repeat(2, auto) 1fr',
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        <Stack gridColumn="span 2">
          <ReportBreadcrumbs title={REPORT_TITLE} />
          <AssetPresenceReportEducationalTitle
            title={REPORT_TITLE}
            reportKind="field"
          />
        </Stack>
        <AssetLocationReportGroupControl
          value={group}
          onChange={handleChangeGroup}
        />
        <ReportDateRangeInput
          interval={fieldReport.interval}
          timezone={user.organization.timezone}
          onChange={handleChangeDateRange}
          hasDateRange={hasDateRange}
          shortcuts={ASSET_PRESENCE_REPORT_DATE_RANGE_SHORTCUTS}
          error={isDateRangeTooLong || isDateRangeError}
          helperText={
            isDateRangeTooLong
              ? `Maximum date range is ${ASSET_PRESENCE_REPORT_MAX_DAYS} days`
              : undefined
          }
        >
          {isDateRangeError && (
            <Tooltip
              arrow
              title="The maximum date range for this report is three years"
            >
              <ErrorRounded color="error" fontSize="small" />
            </Tooltip>
          )}
        </ReportDateRangeInput>
        <Box ref={containerRef} gridColumn="span 2">
          {!hasDateRange ? (
            <Typography
              color="text.secondary"
              sx={{ py: 4, textAlign: 'center' }}
            >
              Select a date range to run the asset report
            </Typography>
          ) : isDateRangeTooLong ? (
            <Typography
              color="text.secondary"
              sx={{ py: 4, textAlign: 'center' }}
            >
              Please select a date range of {ASSET_PRESENCE_REPORT_MAX_DAYS}{' '}
              days or fewer to view this report
            </Typography>
          ) : showLoader ? (
            <AssetReportLoader
              reportType="field"
              periodCount={dayCount}
              isDataReady={!fieldReport.isLoading}
              onComplete={handleLoaderComplete}
            />
          ) : (
            <AssetLocationReportTable
              data={data}
              group={group}
              interval={fieldReport.interval}
              dayCount={fieldReport.data?.dayCount ?? 0}
              isLoading={fieldReport.isLoading}
              maximumHeight={containerSize.height}
              getFileName={getFileName}
            />
          )}
        </Box>
      </PageContent>
    </>
  );
}
